import React, { useEffect, useState } from 'react';
import styled from 'astroturf/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';

type Props = {
  // Allow external styling of the chip
  className?: string;
  children: React.ReactElement;
  name: string;
  width: number;
};

const Backdrop = styled.div`
  background: rgba(0, 0, 0, 0.4);
  height: 100vh;
  left: 0;
  position: fixed;
  top: 0;
  width: 100vw;
  z-index: 2;
`;

const Chip = styled.div<{ active?: boolean }>`
  background: #fff;
  border-radius: 48px;
  color: #4e545a;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  font-size: 14px;
  justify-content: center;
  padding: 1px 17px;

  &.active {
    background: #094ec3;
    color: #fff;
  }
`;

const Icon = styled(FontAwesomeIcon)`
  color: #bbb;
  font-size: 24px;
  position: absolute;
  right: 16px;
  top: 10px;
`;

const Popover = styled.div`
  align-items: center;
  background: #fff;
  box-shadow: 0px 4px 14px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  left: 24px;
  padding: 34px 30px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: calc(100vw - 48px);
  z-index: 3;
`;

// This component will automatically "chipize" a component
// at mobile sizes, ie it will turn it into a chip that can be expanded.
//
// At wider than mobile sizes, show the regular content.
// At mobile sizes, show a chip that, when clicked,
// opens into a small popover that contains the content.
const MobileChipize = ({
  children,
  className,
  name,
  width: showAtWidth,
}: Props) => {
  // Whether to show the popover
  const [show, setShow] = useState(false);
  // Updated on resize
  const [width, setWidth] = useState(window.innerWidth);

  // Scroll lock is currently bugged due to an interaction
  // with the premium modal that shows on page load
  // useScrollLock(show && width <= showAtWidth);

  useEffect(() => {
    const handler = () => {
      setWidth(window.innerWidth);
    };

    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('resize', handler);
    };
  }, []);

  // At wider than mobile widths, just show the content
  if (width > showAtWidth) return children;

  // At less than mobile widths, show the chip
  if (!show) {
    return (
      <Chip className={className} onClick={() => setShow(true)}>
        {name}
      </Chip>
    );
  }
  // Show chip with popover once clicked
  else {
    return (
      <React.Fragment>
        <Backdrop onClick={() => setShow(false)} />
        <Chip active className={className}>
          {name}
          <Popover>
            <Icon icon={faTimes} onClick={() => setShow(false)} />
            {children}
          </Popover>
        </Chip>
      </React.Fragment>
    );
  }
};

export default MobileChipize;
